import { useCallback, useMemo, useRef, useState } from 'react';

import { convertToRaw, EditorState } from 'draft-js';

import { KaseApi } from '@/api';
import { INarrative } from '@/models';
import { ConfirmTypes, useToastStore } from '@/store';
import { useUserStore } from '@/store/store.user';

interface NewNarrative {
  tempId: string;
  editorState: EditorState;
  isEditing: boolean;
}

interface UseArrestNarrativesProps {
  kaseId?: string;
  arresteeId?: string;
  narratives: INarrative[];
  fetchNarratives: () => Promise<void>;
  narrativeToEditorState: (narrative: INarrative) => EditorState;
}

export const useArrestNarratives = ({
  kaseId,
  arresteeId,
  narratives,
  fetchNarratives,
  narrativeToEditorState,
}: UseArrestNarrativesProps) => {
  const { updateToast } = useToastStore();
  const { user } = useUserStore();
  const currentUserId = user?._id?.toString();

  // Standalone saving state (when not in parent edit mode)
  const [isSaving, setIsSaving] = useState(false);

  // Track which existing narratives are in edit mode
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set());
  // Track editor states for existing narratives being edited
  const [editorStates, setEditorStates] = useState<Record<string, EditorState>>(
    {},
  );
  // Track newly added (unsaved) narratives
  const [newNarratives, setNewNarratives] = useState<NewNarrative[]>([]);
  // Store original editor states for dirty checking
  const originalStatesRef = useRef<Record<string, string>>({});

  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletingNarrative, setIsDeletingNarrative] = useState(false);
  const [narrativeToDelete, setNarrativeToDelete] = useState<string | null>(
    null,
  );

  // Check if current user is the creator of a narrative
  const isCreator = useCallback(
    (narrative: INarrative): boolean => {
      if (!currentUserId || !narrative.createdBy) return false;
      const creatorId =
        typeof narrative.createdBy === 'string'
          ? narrative.createdBy
          : (narrative.createdBy as any)?._id?.toString();
      return currentUserId === creatorId;
    },
    [currentUserId],
  );

  // Switch an existing narrative to edit mode
  const handleStartEdit = useCallback(
    (narrative: INarrative) => {
      const id = narrative._id?.toString() || '';
      if (editingIds.has(id)) return;

      const editorState = narrativeToEditorState(narrative);
      // Store original content for dirty checking
      const rawContent = JSON.stringify(
        convertToRaw(editorState.getCurrentContent()),
      );
      originalStatesRef.current[id] = rawContent;

      setEditorStates((prev) => ({ ...prev, [id]: editorState }));
      setEditingIds(new Set([id]));
      setNewNarratives((prev) => prev.map((n) => ({ ...n, isEditing: false })));
    },
    [editingIds, narrativeToEditorState],
  );

  // Update editor state for an existing narrative
  const handleEditorChange = useCallback(
    (id: string, newState: EditorState) => {
      setEditorStates((prev) => ({ ...prev, [id]: newState }));
    },
    [],
  );

  // Update editor state for a new narrative
  const handleNewEditorChange = useCallback(
    (tempId: string, newState: EditorState) => {
      setNewNarratives((prev) =>
        prev.map((n) =>
          n.tempId === tempId ? { ...n, editorState: newState } : n,
        ),
      );
    },
    [],
  );

  // Add a new empty narrative
  const handleAddNew = useCallback(() => {
    setEditingIds(new Set());
    const tempId = `new-${Date.now()}`;
    setNewNarratives((prev) => [
      ...prev.map((n) => ({ ...n, isEditing: false })),
      { tempId, editorState: EditorState.createEmpty(), isEditing: true },
    ]);
  }, []);

  // Switch a new (unsaved) narrative to edit mode
  const handleStartEditNew = useCallback((tempId: string) => {
    setEditingIds(new Set());
    setNewNarratives((prev) =>
      prev.map((n) => ({ ...n, isEditing: n.tempId === tempId })),
    );
  }, []);

  // Delete a new (unsaved) narrative
  const handleDeleteNew = useCallback((tempId: string) => {
    setNewNarratives((prev) => prev.filter((n) => n.tempId !== tempId));
  }, []);

  // Check if a narrative is dirty (content changed from original)
  const isDirty = useCallback(
    (id: string): boolean => {
      const state = editorStates[id];
      if (!state) return false;
      const currentRaw = JSON.stringify(
        convertToRaw(state.getCurrentContent()),
      );
      return currentRaw !== originalStatesRef.current[id];
    },
    [editorStates],
  );

  // Save all dirty narratives (called by parent via ref)
  const saveAll = useCallback(async () => {
    if (!kaseId || !arresteeId) return;

    const promises: Promise<any>[] = [];

    // Save dirty existing narratives
    for (const id of editingIds) {
      if (!isDirty(id)) continue;
      const state = editorStates[id];
      if (!state) continue;

      // Find the original narrative to preserve its type/title
      const narrative = narratives.find((n) => n._id?.toString() === id);
      const rawContent = convertToRaw(state.getCurrentContent());
      const data = {
        title: narrative?.title || 'Arrest Narrative',
        type: narrative?.type || 'SUPPLEMENT',
        content: JSON.stringify({ text: rawContent }),
        _id: id,
      };
      promises.push(
        KaseApi.saveArresteeNarrative(kaseId, arresteeId, data as any),
      );
    }

    // Save new narratives (only those with content)
    for (const { editorState } of newNarratives) {
      const plainText = editorState.getCurrentContent().getPlainText().trim();
      if (!plainText) continue;

      const rawContent = convertToRaw(editorState.getCurrentContent());
      const data = {
        title: 'Arrest Narrative',
        type: 'SUPPLEMENT',
        content: JSON.stringify({ text: rawContent }),
      };
      promises.push(
        KaseApi.saveArresteeNarrative(kaseId, arresteeId, data as any),
      );
    }

    if (promises.length === 0) return;

    const results = await Promise.allSettled(promises);
    const failures = results.filter((r) => r.status === 'rejected');

    if (failures.length > 0) {
      throw new Error(
        `Failed to save ${failures.length} narrative(s). Please try again.`,
      );
    }

    // Clear editing state after successful save
    setEditingIds(new Set());
    setEditorStates({});
    setNewNarratives([]);
    originalStatesRef.current = {};
    await fetchNarratives();
  }, [
    kaseId,
    arresteeId,
    editingIds,
    editorStates,
    newNarratives,
    narratives,
    isDirty,
    fetchNarratives,
  ]);

  // Reset all editing state (called by parent via ref on Cancel)
  const resetAll = useCallback(() => {
    setEditingIds(new Set());
    setEditorStates({});
    setNewNarratives([]);
    originalStatesRef.current = {};
  }, []);

  // Standalone save (when parent form is NOT in edit mode)
  const handleStandaloneSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await saveAll();
      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: 'Narratives saved successfully',
      });
    } catch (error: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: error?.message || 'Failed to save narratives',
      });
    } finally {
      setIsSaving(false);
    }
  }, [saveAll, updateToast]);

  // Delete handlers
  const handleDeleteClick = useCallback((narrativeId: string) => {
    setNarrativeToDelete(narrativeId);
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!narrativeToDelete || !kaseId || !arresteeId) return;

    setIsDeletingNarrative(true);
    try {
      await KaseApi.deleteArresteeNarrative(
        kaseId,
        arresteeId,
        narrativeToDelete,
      );

      updateToast({
        type: ConfirmTypes.SUCCESS,
        open: true,
        message: 'Narrative deleted successfully',
      });

      // Clean up editing state for deleted narrative
      setEditingIds((prev) => {
        const next = new Set(prev);
        next.delete(narrativeToDelete);
        return next;
      });
      setEditorStates((prev) => {
        const next = { ...prev };
        delete next[narrativeToDelete];
        return next;
      });
      delete originalStatesRef.current[narrativeToDelete];

      setIsDeleteModalOpen(false);
      setNarrativeToDelete(null);
      await fetchNarratives();
    } catch (error: any) {
      updateToast({
        type: ConfirmTypes.ERROR,
        open: true,
        message: error?.message || 'Failed to delete narrative',
      });
    } finally {
      setIsDeletingNarrative(false);
    }
  }, [narrativeToDelete, kaseId, arresteeId, updateToast, fetchNarratives]);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteModalOpen(false);
    setNarrativeToDelete(null);
  }, []);

  const hasUnsavedNarratives = useMemo(() => {
    const hasDirtyExistingNarratives = Array.from(editingIds).some((id) =>
      isDirty(id),
    );
    const hasDirtyNewNarratives = newNarratives.some(
      ({ editorState }) =>
        editorState.getCurrentContent().getPlainText().trim().length > 0,
    );

    return hasDirtyExistingNarratives || hasDirtyNewNarratives;
  }, [editingIds, isDirty, newNarratives]);

  return {
    // State
    editingIds,
    editorStates,
    newNarratives,
    hasUnsavedNarratives,
    isSaving,
    isDeleteModalOpen,
    isDeletingNarrative,
    // Handlers
    isCreator,
    handleStartEdit,
    handleEditorChange,
    handleNewEditorChange,
    handleAddNew,
    handleStartEditNew,
    handleDeleteNew,
    saveAll,
    resetAll,
    handleStandaloneSave,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
